import {
    ConvertableUOM,
    IUOMConverter,
    IUOMConverterOptions,
    IUOMConvertOptions,
    UOM
} from "../PA.MD.Services/PA.MD.Services.IF";
import * as _ from "lodash"
import {WB_PlanTypeRow} from "../PA.Core/PAdbSchemaRows";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {esWB_Plan_Type} from "../PA.Core/PAdbSchema";
import {Domain_getItems} from "../PA.Core/PAdbUtils";
import {IwbPlanVolumeMetrics} from "../PA.Core/PAdbSchemaRowsIF";

export class PlanVolumeConverter
    implements
        IUOMConverter
{
    private _bLoaded: boolean
    private _onLoadComplete: Promise<void>
    private _uoms: ConvertableUOM[]
    private _opts: IUOMConverterOptions
    private _planTypeRow: WB_PlanTypeRow
    private _dbContext: PAdbContext
    private _planningQtyUOM: string
    private _planningCurrencyCd: string

    constructor(options?: IUOMConverterOptions) {
        this._bLoaded = false
        this._onLoadComplete = undefined
        this._uoms = []
        this._opts = options || {}
        this._planTypeRow = undefined
    }

    get UOMConverterId(): string { return "PlanVolumeConverter" } // Must Match UOMConverterService
    get UOMs(): ConvertableUOM[] { return this._uoms }
    get bLoaded(): boolean { return  this._bLoaded }

    getDbContext(): PAdbContext {
        if(!_.isEmpty(this._dbContext))
            this._dbContext = new PAdbContext()
        return this._dbContext
    }

    get OnLoadComplete(): Promise<void> {
        const self = this
        let opts = self._opts

        if(this.bLoaded)
            return Promise.resolve()

        if(!_.isUndefined(this._onLoadComplete))
            return self._onLoadComplete

        let returnValue = this._onLoadComplete = (async ()=> {
            // Get the Plan Type, and get the list of convertable UOMs..
            if(!_.isEmpty(opts.WB_PlanTypeRow)){
                self._planTypeRow = opts.WB_PlanTypeRow
            } else if (_.isNumber(opts.WB_PLANM_ID)){
                let dbContext = self.getDbContext()
                self._planTypeRow = await dbContext.find(esWB_Plan_Type, opts.WB_PLANM_ID)
            }
            if(_.isEmpty(self._planTypeRow))
                throw "PlanVolumeConverter: missing Plan Type in constructor parameters"

            let dctUOM = _.keyBy(await Domain_getItems(self.getDbContext(), ['UOM']), (a) => a.cif.DOMAIN_ITEM_ID)
            let dctAmountType = _.keyBy(await Domain_getItems(self.getDbContext(), ['UOMAmountType']), (a) => a.cif.DOMAIN_ITEM_ID)
            let planningQtyUOM = self._planTypeRow.cif.QUANTITY_UOM
            self._planningQtyUOM = planningQtyUOM
            let planningCurrencyCd = self._planTypeRow.cif.CURRENCY
            self._planningCurrencyCd = planningCurrencyCd

            let planningUOM: ConvertableUOM = {
                Name: dctUOM[planningQtyUOM]?.cif?.DOMAIN_ITEM_NAME || planningQtyUOM,
                bUOM: true,
                UOMCode: planningQtyUOM,
                bCurrency: false,
                CurrencyCode: ""
            }

            self._uoms.push(planningUOM)

            // Add Other standard
            let amountTypes = ['CustomerList', 'GrossList']
            if(!_.isEmpty(self._planTypeRow.cif.QUANTITY_CONVERTABLE_UOMS.trim()))
                amountTypes = self._planTypeRow.cif.QUANTITY_CONVERTABLE_UOMS.trim().split(",")

            _.forEach(amountTypes, (sAmountType) => {
                self._uoms.push({
                    Name: dctAmountType[sAmountType]?.cif?.DOMAIN_ITEM_NAME || sAmountType,
                    bUOM: false,
                    UOMCode: "",
                    bCurrency: true,
                    CurrencyCode: planningCurrencyCd,
                    AmountType: sAmountType
                })
            })

            self._bLoaded  = true
            return

        })();

        return returnValue
    }


    conversionRate(fromUOM: UOM, toUOM: UOM, options?: IUOMConvertOptions): Promise<number> | number {
        const self = this

        if(_.isEmpty(fromUOM) || _.isEmpty(toUOM))
            return 1

        let opts = options || {}
        if(fromUOM.bUOM && toUOM.bUOM && (fromUOM.UOMCode == toUOM.UOMCode))
            return 1


        let dataRow: IwbPlanVolumeMetrics = undefined
        if(!_.isEmpty(opts.dataRow))
            dataRow = opts.dataRow
        else if (!_.isEmpty(opts.cubeSourceCell))
            dataRow = opts.cubeSourceCell.getDataRow("PlanVMT")

        if(_.isEmpty(dataRow))
            throw "PlanVMT Data Row not present for conversion factors"

        function getBaseConversionRate(aUOM: UOM){
           if(aUOM.bUOM && (aUOM.UOMCode == self._planningQtyUOM))
               return 1

            if(aUOM.bUOM && (aUOM.UOMCode == "EA"))
                return dataRow.UNIT_FACTOR

            if(aUOM.bCurrency && (aUOM.CurrencyCode == self._planningCurrencyCd) && (aUOM.AmountType == "CustomerList"))
                 return dataRow.NET_LIST_PRICE

            if(aUOM.bCurrency && (aUOM.CurrencyCode == self._planningCurrencyCd) && (aUOM.AmountType == "GrossList"))
                return dataRow.GROSS_LIST_PRICE

            return undefined
        }

        // Using the BaseUOM = planningUOM,
        // -- convert between any combinations using simple FromUOM -> Base UOM -> ToUOM
        let denRate = getBaseConversionRate(fromUOM)
        let numRate = getBaseConversionRate(toUOM)
        if(_.isUndefined(denRate) || _.isUndefined(numRate) || denRate == 0 || !_.isNumber(denRate) || !_.isNumber(numRate))
            return undefined

        return numRate / denRate

    }

    canConvert(fromUOM: UOM, toUOM: UOM): boolean {
        return this.isUOMRecognized(fromUOM) && this.isUOMRecognized(toUOM)
    }

    private isUOMRecognized(aUOM: UOM): boolean {
        const self = this

        if(_.isEmpty(aUOM))
            return false

        if(aUOM.bUOM && (aUOM.UOMCode == self._planningQtyUOM))
            return true

        if(aUOM.bUOM && (aUOM.UOMCode == "EA"))
            return true

        if(aUOM.bCurrency && (aUOM.CurrencyCode == self._planningCurrencyCd) && (aUOM.AmountType == "CustomerList"))
            return true

        if(aUOM.bCurrency && (aUOM.CurrencyCode == self._planningCurrencyCd) && (aUOM.AmountType == "GrossList"))
            return true

        return false

    }



}